import { Box, Grid, Typography, Alert, Button, Chip, IconButton, Tooltip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { activitiesApi, dashboardApi, tasksApi, summariesApi } from '../api';
import type { Task, Activity, DailySummary } from '../types/api';
import KPICard from '../components/KPICard';
import AIBriefingCard from '../components/AIBriefingCard';
import CentralFeed from '../components/CentralFeed';
import DataHealthCard from '../components/DataHealthCard';
import { LoadingState, DashboardLoading } from '../components/LoadingState';
import ErrorBoundary from '../components/ErrorBoundary';
import { useOptimizedDashboard } from '../hooks/useOptimizedQuery';
import { useAuth } from '../contexts/AuthContext';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import AssignmentIcon from '@mui/icons-material/Assignment';
import RefreshIcon from '@mui/icons-material/Refresh';
import SpeedIcon from '@mui/icons-material/Speed';
import WavingHandIcon from '@mui/icons-material/WavingHand';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

// Sales tips
const SALES_TIPS = [
  { category: 'Prospecting', tip: 'The best time to call prospects is Tuesday-Thursday between 10-11am. Your connection rate could increase by 30%!', icon: '📞' },
  { category: 'Follow-up', tip: '80% of sales require 5+ follow-ups, but 44% of reps give up after one. Persistence pays off!', icon: '🔄' },
  { category: 'Closing', tip: 'Ask "What would need to happen for you to move forward?" to uncover hidden objections.', icon: '🎯' },
  { category: 'Relationships', tip: 'Send a personalized video message instead of an email. Response rates jump by 3x!', icon: '🤝' },
  { category: 'Pipeline', tip: 'Review your pipeline weekly. Deals stalled for 2+ weeks have 50% lower close rates.', icon: '📊' },
  { category: 'Discovery', tip: 'Ask "Tell me more about that" to dig deeper. The best insights come from the 3rd or 4th question.', icon: '🔍' },
  { category: 'Mindset', tip: 'Rejection is redirection. Every "no" gets you closer to your next "yes"!', icon: '💪' },
  { category: 'Productivity', tip: 'Block 2 hours each morning for prospecting. Your energy and focus are highest then.', icon: '⏰' },
];

// Motivational quotes
const QUOTES = [
  { quote: "Every sale has five basic obstacles: no need, no money, no hurry, no desire, no trust.", author: "Zig Ziglar" },
  { quote: "Success is walking from failure to failure with no loss of enthusiasm.", author: "Winston Churchill" },
  { quote: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
  { quote: "Don't watch the clock; do what it does. Keep going.", author: "Sam Levenson" },
  { quote: "Your attitude, not your aptitude, will determine your altitude.", author: "Zig Ziglar" },
];

// Helper function for time-based greeting
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

// Get greeting emoji based on time
const getGreetingEmoji = () => {
  const hour = new Date().getHours();
  if (hour < 12) return '☀️';
  if (hour < 17) return '🌤️';
  return '🌙';
};

interface KPIData {
  pipeline_value: number;
  active_deals_count: number;
  open_tasks_count: number;
}

export function DashboardPage() {
  const { user } = useAuth();
  
  // State for rotating tips and quotes
  const [currentTip, setCurrentTip] = useState(SALES_TIPS[0]);
  const [currentQuote, setCurrentQuote] = useState(QUOTES[0]);
  
  // Rotate tip and quote on mount
  useEffect(() => {
    const tipIndex = Math.floor(Math.random() * SALES_TIPS.length);
    const quoteIndex = Math.floor(Math.random() * QUOTES.length);
    setCurrentTip(SALES_TIPS[tipIndex]);
    setCurrentQuote(QUOTES[quoteIndex]);
  }, []);
  
  // Function to refresh tip/quote
  const refreshTipAndQuote = () => {
    const tipIndex = Math.floor(Math.random() * SALES_TIPS.length);
    const quoteIndex = Math.floor(Math.random() * QUOTES.length);
    setCurrentTip(SALES_TIPS[tipIndex]);
    setCurrentQuote(QUOTES[quoteIndex]);
  };
  
  // Use optimized dashboard query (single request vs multiple)
  const {
    data: optimizedData,
    isLoading: isLoadingOptimized,
    error: optimizedError,
    refetch: refetchOptimized
  } = useOptimizedDashboard();

  // Fallback to individual queries if optimized endpoint fails
  const {
    data: kpis,
    isLoading: isLoadingKPIs,
    error: kpisError,
    refetch: refetchKPIs
  } = useQuery<KPIData>({
    queryKey: ['dashboard', 'kpis'],
    queryFn: async () => {
      const response = await dashboardApi.getKPIs();
      return response?.data || response || {};
    },
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: !optimizedData?.success, // Only run if optimized query failed
  });

  // Fetch tasks with better error handling
  const {
    data: tasks = [],
    isLoading: isLoadingTasks,
    error: tasksError,
    refetch: refetchTasks
  } = useQuery<Task[]>({
    queryKey: ['dashboard', 'tasks'],
    queryFn: async () => {
      const response = await tasksApi.getTasks();
      return response?.data || response || [];
    },
    retry: 2,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Fetch latest summary with better error handling
  const {
    data: summary,
    isLoading: isLoadingSummary,
    error: summaryError,
    refetch: refetchSummary
  } = useQuery<DailySummary | null>({
    queryKey: ['summaries', 'latest'],
    queryFn: async () => {
      const response = await summariesApi.getLatestDailySummary();
      return response?.data || response || null;
    },
    retry: 1,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // Fetch recent activities with better error handling
  const {
    data: activities = [],
    isLoading: isLoadingActivities,
    error: activitiesError,
    refetch: refetchActivities
  } = useQuery<Activity[]>({
    queryKey: ['activities', 'recent'],
    queryFn: async () => {
      const response = await activitiesApi.getRecentActivities();
      return response?.data || response || [];
    },
    retry: 2,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Use optimized data if available, fallback to individual queries
  // Note: optimizedData returns { summary: { totalDeals, ... } } format
  // while kpis returns { active_deals_count, ... } format
  const dashboardData = optimizedData?.summary ? {
    total_deals: optimizedData.summary.totalDeals,
    total_contacts: optimizedData.summary.totalContacts,
    pipeline_value: optimizedData.summary.pipelineValue,
    active_deals_count: optimizedData.summary.totalDeals,
  } : kpis;
  const isOptimized = !!optimizedData?.summary;

  // Determine overall loading state
  const isLoading = isOptimized ? isLoadingOptimized : (isLoadingKPIs || isLoadingSummary);
  const hasError = isOptimized ? optimizedError : (kpisError || summaryError);

  // Retry function for errors
  const handleRetry = () => {
    if (isOptimized) {
      refetchOptimized();
    } else {
      refetchKPIs();
      refetchSummary();
    }
    refetchTasks();
    refetchActivities();
  };

  return (
    <ErrorBoundary>
      <LoadingState
        loading={isLoading}
        error={hasError}
        skeleton="dashboard"
        loadingText="Loading your dashboard..."
        errorMessage="Failed to load dashboard data. Please check your connection and try again."
        onRetry={handleRetry}
      >
        <Box sx={{ p: 3 }}>
          {/* Performance Indicator */}
          {isOptimized && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Chip
                icon={<SpeedIcon />}
                label="Performance Optimized"
                color="success"
                variant="outlined"
                size="small"
              />
            </Box>
          )}

          <Grid container spacing={3}>
            {/* Greeting Banner - First element across the top */}
            <Grid item xs={12}>
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 3,
                  p: 3,
                  color: 'white',
                  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
                }}
              >
                {/* Main Greeting Row */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h3" component="span">
                      {getGreetingEmoji()}
                    </Typography>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {getGreeting()}, {user?.displayName || user?.email?.split('@')[0] || 'there'}!
                      </Typography>
                      <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
                        Ready to close some deals today? Let's make it happen! 🚀
                      </Typography>
                    </Box>
                  </Box>
                  <Tooltip title="Refresh tip & quote">
                    <IconButton onClick={refreshTipAndQuote} sx={{ color: 'white' }}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Tip, Quote, and Badges Row */}
                <Grid container spacing={2}>
                  {/* Sales Tip */}
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        borderRadius: 2,
                        p: 2,
                        height: '100%',
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <TipsAndUpdatesIcon sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle2" fontWeight="bold">
                          💡 Tip of the Day
                        </Typography>
                        <Chip label={currentTip.category} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.7rem' }} />
                      </Box>
                      <Typography variant="body2" sx={{ opacity: 0.95 }}>
                        {currentTip.icon} {currentTip.tip}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Motivational Quote */}
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        borderRadius: 2,
                        p: 2,
                        height: '100%',
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <FormatQuoteIcon sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle2" fontWeight="bold">
                          ✨ Daily Inspiration
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontStyle: 'italic', opacity: 0.95 }}>
                        "{currentQuote.quote}"
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8, mt: 0.5, display: 'block' }}>
                        — {currentQuote.author}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Badges/Achievements */}
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        borderRadius: 2,
                        p: 2,
                        height: '100%',
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <EmojiEventsIcon sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle2" fontWeight="bold">
                          🏆 Your Badges
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip label="🎉 First Deal!" size="small" sx={{ bgcolor: 'rgba(255,215,0,0.3)', color: 'white' }} />
                        <Chip label="🔥 Hot Streak" size="small" sx={{ bgcolor: 'rgba(255,100,0,0.3)', color: 'white' }} />
                        <Chip label="📈 Pipeline Pro" size="small" sx={{ bgcolor: 'rgba(0,255,100,0.3)', color: 'white' }} />
                        <Chip label="🤝 Team Player" size="small" sx={{ bgcolor: 'rgba(100,100,255,0.3)', color: 'white' }} />
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Top Row: AI Briefing */}
            <Grid item xs={12}>
              <LoadingState
                loading={isLoadingSummary}
                error={summaryError}
                skeleton="card"
                loadingText="Loading AI briefing..."
                onRetry={refetchSummary}
              >
                <AIBriefingCard
                  summary={summary?.summary_text || null}
                  isLoading={false}
                />
              </LoadingState>
            </Grid>

            {/* Second Row: KPIs */}
            <Grid item xs={12} sm={4}>
              <KPICard
                title="Pipeline Value"
                value={`$${(dashboardData?.pipeline_value || 0).toLocaleString()}`}
                icon={<AttachMoneyIcon fontSize="large" />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <KPICard
                title="Active Deals"
                value={dashboardData?.total_deals || 0}
                icon={<BusinessCenterIcon fontSize="large" />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <KPICard
                title="Total Contacts"
                value={dashboardData?.total_contacts || 0}
                icon={<AssignmentIcon fontSize="large" />}
              />
            </Grid>

            {/* Data Health Card - Shows CRM data quality metrics */}
            <Grid item xs={12} md={4}>
              <DataHealthCard compact />
            </Grid>

            {/* Main Content: Central Feed */}
            <Grid item xs={12} md={8}>
              <LoadingState
                loading={isLoadingTasks || isLoadingActivities}
                error={tasksError || activitiesError}
                skeleton="list"
                skeletonCount={5}
                loadingText="Loading recent activity..."
                onRetry={() => {
                  refetchTasks();
                  refetchActivities();
                }}
              >
                <CentralFeed
                  tasks={tasks || []}
                  activities={activities}
                  isLoading={false}
                />
              </LoadingState>
            </Grid>
          </Grid>
        </Box>
      </LoadingState>
    </ErrorBoundary>
  );
}